// ════════════════════════════════════════════════════════════
// /paiements/new — saisie encaissement (mobile-first, version light ECR)
// ════════════════════════════════════════════════════════════
// Flow :
//   1. User saisit client + montant + mode (wallet/cheque/virement)
//      + comptes + champs mode-specific. Plus de mode « cash » (décision
//      Sidi 2026-09-11) : pas de caisse physique, l'espèce reçue d'un client
//      est versée sur Bankily via un point cash → saisie en Wallet → Bankily.
//      Mode Wallet = compte crédité Bankily ou Caisse Mauripe (wallet
//      Mauripe TP) ; le provider est toujours Bankily (seul wallet actif).
//   2. Photo justificatif facultative — l'OCR lit montant/réfs mais ne
//      choisit JAMAIS le mode ni les comptes (décision Sidi 2026-08-27)
//   3. computeAutoConfirmation calculé côté front (wallet+photo /
//      cheque même banque) → INSERT paiement
//   4. Photo uploadée Storage post-création (kind=photo)
//   5. Redirect + toast + WhatsApp share button
//
// Mobile-first : grands boutons mode, sticky-ish layout.
// ════════════════════════════════════════════════════════════
import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm, Controller, type SubmitHandler } from 'react-hook-form';
import {
  ArrowLeft,
  Loader2,
  Camera,
  Upload,
  AlertTriangle,
  CheckCircle2,
  X,
  Wallet,
  FileText,
  ArrowRightLeft,
  MessageCircle,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import {
  useCreatePaiement,
  useBanques,
  useWalletsProviders,
  useOcrPaiement,
  type CreatePaiementInput,
} from '@/queries/paiements';
import { useComptesTresorerie } from '@/queries/tresorerie';
import { uploadPaiementJustificatif, fileToBase64 } from '@/lib/uploadStorage';
import { isOcrAvailable, type OcrPaiementResult } from '@/lib/aiGateway';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { MoneyInput } from '@/components/ui/MoneyInput';
import { Label } from '@/components/ui/Label';
import { WalletBankPicker } from '@/components/paiements/WalletBankPicker';
import { cn } from '@/lib/utils';
import { formatMRU } from '@ecr/core/utils';
import {
  computeAutoConfirmation,
  describeAutoConfirmRule,
  canCreatePaiement,
  type PaiementMode,
} from '@ecr/core/paiements';

export const Route = createFileRoute('/_authed/paiements/new')({
  staticData: { crumb: 'Nouvel encaissement' },
  component: NewPaiementPage,
});

interface FormShape {
  clientNom: string;
  montant: number;
  mode: PaiementMode;
  banqueEncaissementId: string;
  banqueEmettriceId: string;
  numCheque: string;
  dateCheque: string;
  refTransaction: string;
  dateTransaction: string;
  dateEncaissement: string;
  commentaire: string;
}

function todayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function NewPaiementPage() {
  const navigate = useNavigate();
  const { profile } = useAuth(); // session gérée via mutations Supabase auth.getUser()

  const { data: banques = [] } = useBanques();
  const { data: wallets = [] } = useWalletsProviders();
  const { data: comptes = [] } = useComptesTresorerie();
  const createPaiement = useCreatePaiement();

  // banques = référentiel « banques du marché » (banque émettrice du chèque client)
  const banquesEmettrices = banques;
  // Comptes de trésorerie ECR : chèque/virement → comptes bancaires ;
  // wallet → Bankily (défaut) ou Caisse Mauripe (wallet Mauripe TP).
  const comptesBanque = comptes.filter((c) => c.type === 'banque');
  const compteBankily = comptes.find((c) => c.code === 'BANKILY');
  const comptesWallet = useMemo(
    () =>
      comptes
        .filter((c) => c.code === 'BANKILY' || c.code === 'CAISSE')
        .sort((a, b) => (a.code === 'BANKILY' ? 0 : 1) - (b.code === 'BANKILY' ? 0 : 1)),
    [comptes],
  );
  // Seul wallet provider actif (migration 20260911) — imposé en mode wallet.
  const walletBankily = wallets.find((w) => w.code === 'bankily') ?? wallets[0];

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [createdCode, setCreatedCode] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);

  // OCR du justificatif : lit montant / n° chèque / réf / dates / nom client.
  // Il ne touche NI au mode NI aux comptes (banque émettrice, wallet, compte
  // d'encaissement) — l'utilisateur choisit où l'argent est allé.
  const ocrMutation = useOcrPaiement();
  const [ocrResult, setOcrResult] = useState<OcrPaiementResult | null>(null);
  const [ocrAnalyzing, setOcrAnalyzing] = useState(false);

  const form = useForm<FormShape>({
    defaultValues: {
      clientNom: '',
      montant: 0,
      mode: 'wallet',
      banqueEncaissementId: '',
      banqueEmettriceId: '',
      numCheque: '',
      dateCheque: '',
      refTransaction: '',
      dateTransaction: '',
      dateEncaissement: '',
      commentaire: '',
    },
  });

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = form;

  const mode = watch('mode');
  const banqueEnc = watch('banqueEncaissementId');
  const banqueEm = watch('banqueEmettriceId');

  // Wallet → Bankily présélectionné (modifiable : Bankily ou Caisse Mauripe) ;
  // Chèque/virement → purge une sélection qui n'est pas un compte bancaire.
  useEffect(() => {
    if (mode === 'wallet') {
      if (compteBankily && !comptesWallet.some((c) => c.id === banqueEnc)) {
        setValue('banqueEncaissementId', compteBankily.id);
      }
    } else if (
      (mode === 'cheque' || mode === 'virement') &&
      banqueEnc &&
      !comptes.some((c) => c.type === 'banque' && c.id === banqueEnc)
    ) {
      setValue('banqueEncaissementId', '');
    }
  }, [mode, banqueEnc, compteBankily, comptesWallet, comptes, setValue]);

  // Preview auto-confirmation
  const autoConfirm = computeAutoConfirmation({
    mode,
    hasPhoto: !!originalFile,
    banqueEmettriceId: banqueEm || null,
    banqueEncaissementId: banqueEnc || null,
  });

  // ═════════════════════════════════════════════════════════
  // Photo justificatif (upload manuel, pas d'OCR)
  // ═════════════════════════════════════════════════════════

  function handleFileSelected(file: File) {
    if (file.size > 5_000_000) {
      toast.error('Fichier trop lourd (max 5 MB).');
      return;
    }
    setOriginalFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(typeof reader.result === 'string' ? reader.result : null);
    };
    reader.readAsDataURL(file);
    if (isOcrAvailable()) {
      void analyzeJustificatif(file);
    }
  }

  async function analyzeJustificatif(file: File) {
    setOcrAnalyzing(true);
    setOcrResult(null);
    try {
      const imageBase64 = await fileToBase64(file);
      const res = await ocrMutation.mutateAsync({
        imageBase64,
        mimeType: file.type || 'image/jpeg',
        expectedClientNom: watch('clientNom') || undefined,
      });
      setOcrResult(res);
      applyOcrToForm(res);
    } catch (e) {
      toast.error(
        `Analyse du justificatif échouée : ${e instanceof Error ? e.message : 'inconnu'} — saisis à la main.`,
      );
    } finally {
      setOcrAnalyzing(false);
    }
  }

  /**
   * Pré-remplit montant/réfs/dates/nom client depuis l'OCR sans écraser ce qui
   * est déjà saisi. Ne touche jamais au mode ni aux comptes (banque émettrice,
   * wallet, compte d'encaissement) : l'utilisateur choisit où l'argent est allé.
   */
  function applyOcrToForm(res: OcrPaiementResult) {
    if (res.type === 'cheque') {
      if (res.num_cheque && !watch('numCheque')) setValue('numCheque', res.num_cheque);
      if (res.date_cheque) setValue('dateCheque', res.date_cheque);
      if (res.tireur && !watch('clientNom')) setValue('clientNom', res.tireur);
    } else if (res.type === 'wallet') {
      if (res.ref_transaction && !watch('refTransaction'))
        setValue('refTransaction', res.ref_transaction);
      if (res.date_transaction) {
        const dt = res.date_transaction.replace(' ', 'T').slice(0, 16);
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dt)) setValue('dateTransaction', dt);
      }
      if (res.expediteur_nom && !watch('clientNom')) setValue('clientNom', res.expediteur_nom);
    }
    if (res.montant != null && res.montant > 0 && !watch('montant')) {
      setValue('montant', res.montant);
    }
    if (res.capture_suspecte) {
      toast.warning(
        `⚠️ Capture suspecte : ${res.capture_suspecte_raison ?? 'vérifie l’original avec le client.'}`,
      );
    }
    if (res.expediteur_mismatch?.detected) {
      toast.warning(
        `⚠️ L’expéditeur de la capture (${res.expediteur_nom ?? '?'}) ne ressemble pas au client saisi.`,
      );
    }
  }

  function clearFile() {
    setOriginalFile(null);
    setImagePreview(null);
    setOcrResult(null);
  }

  // ═════════════════════════════════════════════════════════
  // Submit
  // ═════════════════════════════════════════════════════════

  const onSubmit: SubmitHandler<FormShape> = async (values) => {
    // Mode-specific validation
    if (values.mode === 'cheque' && !values.numCheque) {
      toast.error('N° chèque obligatoire pour mode chèque');
      return;
    }
    // Wallet : provider toujours Bankily (seul wallet actif) — l'argent atterrit
    // sur Bankily ou sur la Caisse Mauripe (wallet Mauripe TP), au choix.
    const walletProviderId = values.mode === 'wallet' ? (walletBankily?.id ?? null) : null;
    if (values.mode === 'wallet' && !walletProviderId) {
      toast.error('Wallet Bankily introuvable dans le référentiel — contacte le DAF');
      return;
    }
    // Compte d'encaissement : toujours requis (trésorerie par compte).
    const compteEncaissementId =
      values.mode === 'wallet' && !comptesWallet.some((c) => c.id === values.banqueEncaissementId)
        ? (compteBankily?.id ?? '')
        : values.banqueEncaissementId;
    if (!compteEncaissementId) {
      toast.error("Compte d'encaissement obligatoire");
      return;
    }

    try {
      const ac = computeAutoConfirmation({
        mode: values.mode,
        hasPhoto: !!originalFile,
        banqueEmettriceId: values.banqueEmettriceId || null,
        banqueEncaissementId: values.banqueEncaissementId || null,
      });

      const input: CreatePaiementInput = {
        // Valeur legacy DB — l'activité n'est plus exposée dans l'UI ECR.
        activite: 'gravier',
        clientNom: values.clientNom.trim(),
        montant: values.montant,
        mode: values.mode,
        commentaire: values.commentaire.trim() || null,
        argentRecu: ac.argentRecu,
        argentRecuAuto: ac.argentRecuAuto,
        argentRecuAutoReason: ac.argentRecuAutoReason,
        banqueEncaissementId: compteEncaissementId,
        // L'OCR peut avoir pré-rempli des champs d'un autre mode (photo chèque
        // mais mode wallet choisi, etc.) — on n'envoie que ceux du mode retenu.
        banqueEmettriceId: values.mode === 'cheque' ? values.banqueEmettriceId || null : null,
        walletProviderId,
        numCheque: values.mode === 'cheque' ? values.numCheque || null : null,
        dateCheque: values.mode === 'cheque' ? values.dateCheque || null : null,
        refTransaction:
          values.mode === 'wallet' || values.mode === 'virement'
            ? values.refTransaction || null
            : null,
        dateTransaction:
          (values.mode === 'wallet' || values.mode === 'virement') && values.dateTransaction
            ? new Date(values.dateTransaction).toISOString()
            : null,
        dateEncaissement: values.dateEncaissement || null,
        imputations: null,
        photoUrl: null, // sera patché après upload
      };

      const paiement = await createPaiement.mutateAsync(input);

      // Upload photo dans Storage post-création
      if (originalFile) {
        try {
          const { path } = await uploadPaiementJustificatif({
            tenantId: paiement.tenantId,
            paiementId: paiement.id,
            kind: 'photo',
            file: originalFile,
          });
          const sbAny = supabase as unknown as SupabaseClient;
          await sbAny.from('paiements').update({ photo_url: path }).eq('id', paiement.id);
        } catch (e) {
          toast.warning(
            `Encaissement créé mais photo non archivée : ${e instanceof Error ? e.message : 'inconnu'}`,
          );
        }
      }

      toast.success(`Encaissement ${paiement.code} créé`);
      setCreatedCode(paiement.code);
      setCreatedId(paiement.id);
    } catch (e) {
      toast.error(`Erreur création : ${e instanceof Error ? e.message : 'inconnu'}`);
    }
  };

  // Saisir un encaissement = DAF only (Sidi/Abidine).
  if (profile && !canCreatePaiement(profile.role)) {
    return (
      <div className="mx-auto max-w-md space-y-4 py-12">
        <Card>
          <CardContent className="space-y-3 py-6 text-center">
            <p className="text-foreground font-medium">Réservé au DAF</p>
            <p className="text-muted-foreground text-sm">
              Seuls Sidi et Abidine saisissent les encaissements.
            </p>
            <Link to="/paiements">
              <Button variant="outline" className="mt-2">
                Retour aux encaissements
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Si encaissement créé → affiche écran post-create avec WhatsApp share
  if (createdCode && createdId) {
    return (
      <PostCreatePanel
        code={createdCode}
        id={createdId}
        clientNom={watch('clientNom')}
        montant={watch('montant')}
        onContinue={() => {
          void navigate({ to: '/paiements/$id', params: { id: createdId } });
        }}
      />
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center gap-3">
        <Link to="/paiements">
          <Button variant="ghost" size="icon" aria-label="Retour">
            <ArrowLeft className="size-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-foreground text-xl font-semibold tracking-tight sm:text-2xl">
            Nouvel encaissement
          </h1>
          <p className="text-muted-foreground text-sm">Saisis puis vérifie avant de créer</p>
        </div>
      </div>

      <form
        onSubmit={(e) => void handleSubmit(onSubmit)(e)}
        className="space-y-5 pb-[calc(9rem+env(safe-area-inset-bottom,0px))] sm:pb-0"
        noValidate
      >
        {/* Client + montant */}
        <Card>
          <CardContent className="space-y-3 py-4">
            <div>
              <Label htmlFor="clientNom">Client</Label>
              <Input
                id="clientNom"
                {...register('clientNom', {
                  required: 'Client obligatoire',
                  minLength: { value: 1, message: 'Min 1 char' },
                })}
                placeholder="Nom du client"
              />
              {errors.clientNom && (
                <p className="mt-1 text-xs text-red-600">{errors.clientNom.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="montant">Montant (MRU)</Label>
              <Controller
                name="montant"
                control={control}
                rules={{
                  required: 'Montant obligatoire',
                  min: { value: 0.01, message: 'Montant > 0' },
                }}
                render={({ field }) => (
                  <MoneyInput
                    id="montant"
                    value={field.value}
                    onChange={(v) => field.onChange(v ?? undefined)}
                    onBlur={field.onBlur}
                  />
                )}
              />
              {errors.montant && (
                <p className="mt-1 text-xs text-red-600">{errors.montant.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Mode — 3 boutons (plus de « Cash » : l'espèce = versement Bankily → Wallet) */}
        <Card>
          <CardContent className="space-y-3 py-4">
            <p className="text-foreground text-sm font-medium">Mode de paiement</p>
            <div className="grid grid-cols-3 gap-2">
              <ModeButton
                icon={FileText}
                label="Chèque"
                active={mode === 'cheque'}
                onClick={() => setValue('mode', 'cheque')}
              />
              <ModeButton
                icon={Wallet}
                label="Wallet"
                active={mode === 'wallet'}
                onClick={() => setValue('mode', 'wallet')}
              />
              <ModeButton
                icon={ArrowRightLeft}
                label="Virement"
                active={mode === 'virement'}
                onClick={() => setValue('mode', 'virement')}
              />
            </div>
            <p className="text-muted-foreground text-xs italic">{describeAutoConfirmRule(mode)}</p>
          </CardContent>
        </Card>

        {/* Mode-specific fields */}
        <Card>
          <CardContent className="space-y-3 py-4">
            {/* Chèque */}
            {mode === 'cheque' && (
              <>
                <div>
                  <Label htmlFor="numCheque">N° chèque</Label>
                  <Input id="numCheque" {...register('numCheque')} />
                </div>
                <div>
                  <Label htmlFor="banqueEmettriceId">Banque émettrice (banque du client)</Label>
                  <select
                    id="banqueEmettriceId"
                    {...register('banqueEmettriceId')}
                    className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
                  >
                    <option value="">— Sélectionner —</option>
                    {banquesEmettrices.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="banqueEncaissementId">Compte d&apos;encaissement</Label>
                  <WalletBankPicker
                    kind="bank"
                    ariaLabel="Compte d'encaissement"
                    options={comptesBanque.map((c) => ({
                      value: c.id,
                      label: c.label,
                      logoKey: c.logoKey,
                    }))}
                    value={watch('banqueEncaissementId') ?? ''}
                    onChange={(v) => setValue('banqueEncaissementId', v, { shouldValidate: true })}
                  />
                </div>
                <div>
                  <Label htmlFor="dateCheque">Date chèque</Label>
                  <Input id="dateCheque" type="date" {...register('dateCheque')} />
                </div>
              </>
            )}

            {/* Wallet */}
            {mode === 'wallet' && (
              <>
                <div>
                  <Label>Compte crédité</Label>
                  <WalletBankPicker
                    ariaLabel="Compte crédité"
                    options={comptesWallet.map((c) => ({
                      value: c.id,
                      label: c.label,
                      logoKey: c.logoKey,
                    }))}
                    value={watch('banqueEncaissementId') ?? ''}
                    onChange={(v) => setValue('banqueEncaissementId', v, { shouldValidate: true })}
                  />
                  <p className="text-muted-foreground mt-1.5 text-xs">
                    Espèces reçues d&apos;un client : versement sur Bankily (point cash), à saisir
                    ici avec la capture du versement.
                  </p>
                </div>
                <div>
                  <Label htmlFor="refTransaction">Référence transaction</Label>
                  <Input id="refTransaction" {...register('refTransaction')} />
                </div>
                <div>
                  <Label htmlFor="dateTransaction">Date transaction</Label>
                  <Input
                    id="dateTransaction"
                    type="datetime-local"
                    {...register('dateTransaction')}
                  />
                </div>
              </>
            )}

            {/* Virement */}
            {mode === 'virement' && (
              <>
                <div>
                  <Label htmlFor="banqueEncaissementId">Compte d&apos;encaissement</Label>
                  <WalletBankPicker
                    kind="bank"
                    ariaLabel="Compte d'encaissement"
                    options={comptesBanque.map((c) => ({
                      value: c.id,
                      label: c.label,
                      logoKey: c.logoKey,
                    }))}
                    value={watch('banqueEncaissementId') ?? ''}
                    onChange={(v) => setValue('banqueEncaissementId', v, { shouldValidate: true })}
                  />
                </div>
                <div>
                  <Label htmlFor="refTransaction">Référence virement</Label>
                  <Input id="refTransaction" {...register('refTransaction')} />
                </div>
                <div>
                  <Label htmlFor="dateTransaction">Date virement</Label>
                  <Input
                    id="dateTransaction"
                    type="datetime-local"
                    {...register('dateTransaction')}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Photo justificatif (manuel, optionnel) */}
        <Card>
          <CardContent className="space-y-3 py-4">
            <p className="text-foreground text-sm font-medium">Photo justificatif (optionnel)</p>
            {originalFile ? (
              <div className="flex items-start gap-3">
                {imagePreview && (
                  <img
                    src={imagePreview}
                    alt="preview"
                    className="size-16 shrink-0 rounded-md border object-cover"
                  />
                )}
                <div className="flex-1 text-sm">
                  <p className="text-foreground font-medium">{originalFile.name}</p>
                  <p className="text-muted-foreground text-xs">
                    {Math.round(originalFile.size / 1024)} KB — sera archivée avec
                    l&apos;encaissement
                  </p>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={clearFile}>
                  <X className="size-4" />
                </Button>
              </div>
            ) : null}
            {originalFile && ocrAnalyzing && (
              <p className="text-muted-foreground flex items-center gap-2 text-xs">
                <Loader2 className="size-3 animate-spin" />
                Analyse du justificatif (type, montant)…
              </p>
            )}
            {originalFile && ocrResult && !ocrAnalyzing && (
              <div
                className={cn(
                  'space-y-1 rounded-md border p-2.5 text-xs',
                  ocrResult.capture_suspecte
                    ? 'border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100'
                    : 'border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-100',
                )}
              >
                <p className="flex flex-wrap items-center gap-1.5 font-medium">
                  <Sparkles className="size-3.5" />
                  {ocrResult.type === 'wallet'
                    ? `Capture wallet détectée (${ocrResult.wallet_type ?? 'bankily'})`
                    : ocrResult.type === 'cheque'
                      ? 'Chèque / remise de chèque détecté'
                      : 'Document non identifié'}
                  <span className="rounded-full bg-black/10 px-1.5 py-0.5 text-[10px] uppercase dark:bg-white/10">
                    confiance {ocrResult.confiance}
                  </span>
                </p>
                <p>
                  {ocrResult.montant != null && (
                    <>
                      Montant lu : <strong>{formatMRU(ocrResult.montant)}</strong> ·{' '}
                    </>
                  )}
                  {ocrResult.type === 'cheque' && ocrResult.num_cheque && (
                    <>Chèque n° {ocrResult.num_cheque} · </>
                  )}
                  {ocrResult.type === 'wallet' && ocrResult.ref_transaction && (
                    <>Réf {ocrResult.ref_transaction} · </>
                  )}
                  montant et références pré-remplis — choisis toi-même le mode et le compte
                  d&apos;encaissement, puis vérifie avant de créer.
                </p>
                {ocrResult.capture_suspecte && (
                  <p className="font-semibold">
                    ⚠️ Capture possiblement falsifiée :{' '}
                    {ocrResult.capture_suspecte_raison ?? 'vérifie l’original avec le client.'}
                  </p>
                )}
              </div>
            )}
            {!originalFile && (
              <div className="flex flex-wrap gap-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleFileSelected(f);
                    }}
                  />
                  <Button asChild type="button" variant="outline" className="gap-2">
                    <span>
                      <Camera className="size-4" />
                      Caméra mobile
                    </span>
                  </Button>
                </label>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleFileSelected(f);
                    }}
                  />
                  <Button asChild type="button" variant="outline" className="gap-2">
                    <span>
                      <Upload className="size-4" />
                      Choisir un fichier
                    </span>
                  </Button>
                </label>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Auto-confirmation preview */}
        <Card
          className={cn(
            autoConfirm.argentRecu
              ? 'border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30'
              : 'border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-950/30',
          )}
        >
          <CardContent className="flex items-start gap-3 py-3">
            {autoConfirm.argentRecu ? (
              <CheckCircle2 className="size-5 shrink-0 text-green-600 dark:text-green-400" />
            ) : (
              <AlertTriangle className="size-5 shrink-0 text-yellow-600 dark:text-yellow-400" />
            )}
            <p className="text-sm">
              {autoConfirm.argentRecu
                ? 'Cet encaissement passera automatiquement en caisse du jour.'
                : 'Attend la confirmation comptable (bordereau bancaire à uploader).'}
            </p>
          </CardContent>
        </Card>

        {/* Date encaissement + commentaire */}
        <Card>
          <CardContent className="space-y-3 py-4">
            <div>
              <Label htmlFor="dateEncaissement">
                Date encaissement (optionnel, saisie tardive = rattrapage)
              </Label>
              <Input
                id="dateEncaissement"
                type="date"
                {...register('dateEncaissement')}
                max={todayYmd()}
              />
            </div>
            <div>
              <Label htmlFor="commentaire">Commentaire (optionnel)</Label>
              <textarea
                id="commentaire"
                {...register('commentaire')}
                rows={2}
                className="border-input bg-background block w-full rounded-md border px-3 py-2 text-sm"
              />
            </div>
          </CardContent>
        </Card>

        {/* Sticky submit bar — au-dessus de la bottom nav mobile (h-16) */}
        <div className="bg-background border-border fixed inset-x-0 bottom-16 z-40 flex items-center justify-between gap-2 border-t p-3 pb-[env(safe-area-inset-bottom,0)] sm:relative sm:bottom-0 sm:justify-end sm:border-0 sm:p-0">
          <Link to="/paiements" className="flex-1 sm:flex-initial">
            <Button type="button" variant="outline" className="w-full">
              Annuler
            </Button>
          </Link>
          <Button type="submit" disabled={isSubmitting} className="flex-1 gap-2 sm:flex-initial">
            {isSubmitting && <Loader2 className="size-4 animate-spin" />}
            Créer l&apos;encaissement
          </Button>
        </div>
      </form>
    </div>
  );
}

// ─── Mode button ─────────────────────────────────────────
function ModeButton({
  icon: Icon,
  label,
  active,
  onClick,
}: {
  icon: typeof Wallet;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col items-center gap-1.5 rounded-md border p-3 transition-all',
        active
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-input text-muted-foreground hover:bg-accent',
      )}
    >
      <Icon className="size-5" />
      <span className="text-xs font-medium">{label}</span>
    </button>
  );
}

// ─── Post-create panel (WhatsApp share) ──────────────────
function PostCreatePanel({
  code,
  clientNom,
  montant,
  onContinue,
}: {
  code: string;
  id: string;
  clientNom: string;
  montant: number;
  onContinue: () => void;
}) {
  const waText = encodeURIComponent(
    `Encaissement ${code} · ${clientNom} · ${formatMRU(montant)} (créé)`,
  );
  return (
    <div className="mx-auto max-w-md space-y-4 py-12">
      <Card className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30">
        <CardContent className="space-y-3 py-6 text-center">
          <CheckCircle2 className="mx-auto size-12 text-green-600 dark:text-green-400" />
          <p className="text-foreground text-lg font-semibold">Encaissement {code} créé</p>
          <p className="text-muted-foreground text-sm">
            {clientNom} · {formatMRU(montant)}
          </p>
          <a
            href={`https://wa.me/?text=${waText}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
          >
            <MessageCircle className="size-4" />
            Partager sur WhatsApp
          </a>
          <div className="flex gap-2">
            <Button type="button" variant="outline" className="flex-1" onClick={onContinue}>
              Voir détail
            </Button>
            <Link to="/paiements" className="flex-1">
              <Button type="button" variant="outline" className="w-full">
                Retour liste
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
